// Printable QR Sheet — Hospitality Experience Engine (iter168)
//
// A4-friendly admin page that batches QR cards across categories/areas for
// front-desk deployment (Room 101–120, Pool, Spa, Coffee Shop, …).
//
// Flow:
//   1. Lists every CX category → area for the current tenant.
//   2. Admin selects which areas to print via checkboxes (default: all).
//   3. Fetches each area's QR PNG (admin-auth blob) and renders 4 cards/row,
//      8 per A4 page with `page-break-inside: avoid`.
//   4. Native `window.print()` produces a deployment-ready PDF.
import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useBranding } from '../../contexts/BrandingContext';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Printer, ArrowLeft, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function CxPrintQr() {
  const { getHeaders } = useAuth();
  const { logoUrl: platformLogoUrl, platformName } = useBranding();
  const headers = useMemo(() => getHeaders(), [getHeaders]);
  const [loading, setLoading] = useState(true);
  const [cats, setCats] = useState([]);
  const [areas, setAreas] = useState([]);
  // Map area_id → object URL of its QR PNG (lazy-fetched).
  const [qrBlobs, setQrBlobs] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [fetching, setFetching] = useState(false);

  // Initial load — categories + every area in one batch.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [c, a] = await Promise.all([
          axios.get(`${API}/cx/categories`, { headers }),
          axios.get(`${API}/cx/areas`, { headers }),
        ]);
        if (cancelled) return;
        setCats(c.data || []);
        setAreas(a.data || []);
        // Default: select every area.
        setSelected(new Set((a.data || []).map((x) => x.id)));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [headers]);

  // Fetch all selected QR PNGs in parallel. Cached so re-selecting the same
  // area doesn't re-download.
  useEffect(() => {
    if (selected.size === 0) return;
    let cancelled = false;
    setFetching(true);
    (async () => {
      const missing = [...selected].filter((id) => !qrBlobs[id]);
      if (missing.length === 0) { setFetching(false); return; }
      const results = await Promise.all(missing.map(async (id) => {
        try {
          const r = await axios.get(`${API}/cx/areas/${id}/qr.png?size=10`,
            { headers, responseType: 'blob' });
          return [id, URL.createObjectURL(r.data)];
        } catch { return [id, null]; }
      }));
      if (cancelled) return;
      setQrBlobs((prev) => {
        const next = { ...prev };
        results.forEach(([id, url]) => { if (url) next[id] = url; });
        return next;
      });
      setFetching(false);
    })();
    return () => { cancelled = true; };
    // Intentionally depend on selected size to refetch when set grows. Using
    // .size avoids re-running when ordering of set entries changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected.size, headers]);

  // Revoke blob URLs on unmount to avoid memory leaks.
  useEffect(() => () => {
    Object.values(qrBlobs).forEach((u) => { try { URL.revokeObjectURL(u); } catch { /* noop */ } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const catById = useMemo(() => {
    const m = {}; cats.forEach((c) => { m[c.id] = c; }); return m;
  }, [cats]);

  const areasByCat = useMemo(() => {
    const m = {};
    areas.forEach((a) => {
      if (!m[a.category_id]) m[a.category_id] = [];
      m[a.category_id].push(a);
    });
    return m;
  }, [areas]);

  const toggleArea = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleCategory = (catId) => {
    const catAreas = areasByCat[catId] || [];
    const allSelected = catAreas.every((a) => selected.has(a.id));
    setSelected((prev) => {
      const next = new Set(prev);
      catAreas.forEach((a) => { allSelected ? next.delete(a.id) : next.add(a.id); });
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(areas.map((a) => a.id)));
  const selectNone = () => setSelected(new Set());

  const selectedAreas = useMemo(
    () => areas.filter((a) => selected.has(a.id)),
    [areas, selected]
  );

  const handlePrint = useCallback(() => {
    // Wait for all QR blobs to be ready before printing.
    if (fetching) return;
    window.print();
  }, [fetching]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-[#5C6B64]">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="cx-print-qr-page">
      {/* Print-only stylesheet — hides controls, fits A4. */}
      <style>{`
        @media print {
          .cx-no-print { display: none !important; }
          body { background: white !important; }
          .cx-print-card { break-inside: avoid; page-break-inside: avoid; }
          @page { size: A4; margin: 12mm; }
        }
        .cx-print-card { box-shadow: 0 1px 2px rgba(0,0,0,0.04); }
      `}</style>

      {/* ─── Controls (hidden on print) ─── */}
      <div className="cx-no-print mb-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <Link to="/admin/cx" className="inline-flex items-center gap-1 text-[12.5px] text-[#5C6B64] hover:text-[color:var(--color-primary)]"
              data-testid="cx-print-back-link">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to Customer Pulse Index
            </Link>
            <h1 className="text-xl font-semibold text-[#1A2421] mt-1">CPI · Printable QR Sheet</h1>
            <p className="text-[12.5px] text-[#5C6B64] mt-0.5">
              Select areas and print A4 sheets for front-desk deployment.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={selectAll} data-testid="cx-print-select-all">
              Select all
            </Button>
            <Button variant="outline" size="sm" onClick={selectNone} data-testid="cx-print-select-none">
              Clear
            </Button>
            <Button onClick={handlePrint} disabled={fetching || selected.size === 0}
              className="bg-[color:var(--color-primary)] hover:bg-[#0B2A22] text-white"
              data-testid="cx-print-trigger">
              {fetching ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Printer className="w-3.5 h-3.5 mr-1.5" />}
              {fetching ? 'Preparing…' : `Print ${selected.size} QR${selected.size === 1 ? '' : 's'}`}
            </Button>
          </div>
        </div>

        {/* Selection grouped by category */}
        <div className="bg-white border border-[#E2E8DF] rounded-lg p-4 space-y-3">
          {cats.length === 0 && (
            <p className="text-[13px] text-[#5C6B64] text-center py-4">
              No categories yet. Go to{' '}
              <Link to="/admin/cx" className="text-[color:var(--color-primary)] underline">Customer Pulse Index</Link> to set them up first.
            </p>
          )}
          {cats.map((c) => {
            const catAreas = areasByCat[c.id] || [];
            const allSel = catAreas.length > 0 && catAreas.every((a) => selected.has(a.id));
            const partialSel = !allSel && catAreas.some((a) => selected.has(a.id));
            return (
              <div key={c.id} className="border-b last:border-b-0 border-[#F0F2EE] pb-3 last:pb-0">
                <label className="flex items-center gap-2 cursor-pointer"
                  data-testid={`cx-print-cat-${c.id}`}>
                  <Checkbox checked={allSel} onCheckedChange={() => toggleCategory(c.id)}
                    className={partialSel ? 'opacity-60' : ''} />
                  <span className="font-medium text-[#1A2421] text-[13.5px]">{c.name}</span>
                  <span className="text-[11px] text-[#5C6B64]">
                    ({catAreas.filter((a) => selected.has(a.id)).length}/{catAreas.length} selected)
                  </span>
                </label>
                {catAreas.length > 0 && (
                  <div className="mt-2 ml-6 flex flex-wrap gap-2">
                    {catAreas.map((a) => (
                      <label key={a.id} className="inline-flex items-center gap-1.5 text-[12px] text-[#5C6B64] cursor-pointer"
                        data-testid={`cx-print-area-${a.id}`}>
                        <Checkbox checked={selected.has(a.id)} onCheckedChange={() => toggleArea(a.id)} />
                        <span>{a.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ─── Print canvas: 2-col grid (4 per A4 row × 4 rows ≈ 8 cards/page) ─── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="cx-print-canvas">
        {selectedAreas.length === 0 && (
          <div className="col-span-full text-center py-8 text-[#5C6B64] text-[13px] cx-no-print">
            Select at least one area to preview QR cards.
          </div>
        )}
        {selectedAreas.map((a) => {
          const cat = catById[a.category_id];
          const qrUrl = qrBlobs[a.id];
          return (
            <div key={a.id}
              className="cx-print-card border border-[#E2E8DF] bg-white rounded-lg p-5 text-center"
              data-testid={`cx-print-card-${a.id}`}>
              {/* Header — brand + category */}
              <div className="flex items-center justify-between mb-3">
                <img src={platformLogoUrl} alt={platformName}
                  className="h-6 w-auto max-w-[80px] object-contain opacity-80" />
                <div className="text-[9px] uppercase tracking-[0.18em] text-[#5C6B64] font-medium">
                  {cat?.name || 'Feedback'}
                </div>
              </div>
              {/* Area name */}
              <h2 className="text-[17px] font-semibold text-[color:var(--color-primary)] mb-3">{a.name}</h2>
              {/* QR */}
              <div className="flex items-center justify-center mb-3">
                {qrUrl ? (
                  <img src={qrUrl} alt={`QR for ${a.name}`}
                    className="w-44 h-44 object-contain border border-[#E2E8DF] rounded-md p-1.5 bg-white" />
                ) : (
                  <div className="w-44 h-44 border border-[#E2E8DF] rounded-md flex items-center justify-center bg-[#F9F9F8]">
                    <Loader2 className="w-4 h-4 animate-spin text-[#5C6B64]" />
                  </div>
                )}
              </div>
              {/* Footer */}
              <div className="text-[10.5px] text-[#5C6B64] leading-snug">
                Scan with your phone camera to share feedback.
              </div>
              <div className="text-[8.5px] text-[#9AAA9F] mt-1 font-mono">{a.qr_token}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
